<script setup lang="ts">
import { ref, toRef, computed, watch, storeToRefs } from '#imports';

import { useDialogQuoteStore } from './../store';
import { useUserAuthStore } from '@/entities/User/store/auth';
import { useArticlesStore } from '@/entities/Article/store';
import articleService from '@/entities/Article/api';

import getDatetime from '@/shared/utils/get-datetime';
import valueFragile from '@/shared/utils/value-fragile';

import IconDownload from '@/assets/icons/icon-download.svg';
import IconCopy from '@/assets/icons/icon-copy.svg';

const props = withDefaults(defineProps<{
	modelValue: boolean;
}>(), {
	modelValue: false,
});

const isOpen = toRef(props, 'modelValue');

const emit = defineEmits<{
	(e: 'update:modelValue'): void;
}>();

const value = computed({
	get: () => props.modelValue,
	set: () => emit('update:modelValue'),
});

const isCopied = ref(false);

/** Data */
const userAuthStore = useUserAuthStore();
const { isAuth } = storeToRefs(userAuthStore);

const articleStore = useArticlesStore();
const { articleById, openArticleId, isEmptyArticleId } = storeToRefs(articleStore);

const dialogQuoteStore = useDialogQuoteStore();
const { linkFormats, link } = storeToRefs(dialogQuoteStore);
const { getBibliographyLink } = dialogQuoteStore;

const selectModel = ref('');
const openArticle = ref();

watch(isOpen, async (val) => {
	if (val) {
		selectModel.value = linkFormats.value[0]?.id ?? '';
		openArticle.value = articleById.value(
			valueFragile(openArticleId.value, 'dialog-action-quote, articleById, openArticleId'),
			'article',
		) || {};

		if (!isEmptyArticleId.value) {
			await getBibliographyLink({
				article_id: valueFragile(openArticleId.value, 'dialog-action-quote, getBibliographyLink, article_id'),
				format_link: selectModel.value,
			});
		}
	} else {
		isCopied.value = false;
	}
}, { immediate: true });

const computedShareLink = computed(() => {
	return `${window.location.origin}/dictionary/${openArticle.value.slug_path}`;
});

const PUBLIC_DATA = 'Дата обращения:';
const computedShareCopyContent = computed(() => {
	return `${link.value} - URL: ${computedShareLink.value}. ${PUBLIC_DATA} ${new Date().toLocaleDateString('ru-RU')}.`;
});

const handleCopy = () => {
	navigator.clipboard.writeText(computedShareCopyContent.value);
	isCopied.value = true;
};

const RESTRICTED_DICTIONARY = 'Ислам в Российской Федерации: энциклопедический словарь';

const canDownload = computed(() => {
    return Boolean(
        openArticle.value?.file
        && (isAuth.value || openArticle.value?.dictionary_name !== RESTRICTED_DICTIONARY),
    );
});
</script>

<template>
	<v-dialog v-model="value">
		<div class="dialog-action-quote">
			<ul class="dialog-action-quote__action">
				<li class="dialog-action-quote__action-item">
					<!-- TODO: border-color -->
					<v-select
						v-model="selectModel"
						:options="linkFormats"
						class="dialog-action-quote__action-select"
					/>
				</li>
				<li class="dialog-action-quote__action-item">
					<v-button
						:disabled="!canDownload"
						@click="articleService.fetchArticleFile(openArticle?.file, `${openArticle?.name}.pdf`)"
					>
						Скачать

						<template #sufix>
							<icon-download class="icon" />
						</template>
					</v-button>
				</li>
				<li class="dialog-action-quote__action-item">
					<v-button
						:variations="['icon']"
						:color="isCopied ? 'green' : undefined"
						@click="handleCopy"
					>
						<icon-copy class="icon" />
					</v-button>
				</li>
			</ul>

			<div class="dialog-action-quote__body">
				<h2 class="dialog-action-quote__title">
					Библиографическая ссылка на источник
				</h2>

				<p class="dialog-action-quote__text">
					{{ link }} - URL:
					<nuxt-link
						:to="computedShareLink"
						target="_blank"
						external
						class="dialog-action-quote__link"
					>
						{{ computedShareLink }}
					</nuxt-link>
				</p>

				<time :datetime="getDatetime(Date.now())">
					{{ PUBLIC_DATA }} {{ new Date().toLocaleDateString('ru-RU') }}.
				</time>
			</div>
		</div>
	</v-dialog>
</template>

<style lang="scss" scoped>
.dialog-action-quote {
	&__action {
		display: flex;
		align-items: center;
		column-gap: rem(12px);

		margin-bottom: rem($gap-medium);
	}

	&__title {
		@include text-base(700);

		margin-bottom: rem($gap-mini);
	}

	&__text {
		margin-bottom: rem($gap-medium);

		word-break: break-word;
	}

	&__link {
		word-break: break-all;
	}
}
</style>
